import { CSSResultGroup, LitElement, TemplateResult } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { scheduleFormTemplate } from "./scheduleForm.html";
import { revalidateLogic, TanStackFormController } from "@tanstack/lit-form";
import { scheduleFormStyles } from "./scheduleForm.css";
import { ObjectTree } from "../../../trend/objecttree";
import { EditRepeatRuleModal } from "../../modals/editRepeatRuleModal/editRepeatRuleModal";
import { getDefaultRecurrenceRule, RecurrenceEndType, RecurrenceRule, recurrenceRuleValidationSchema } from "../../types/recurrenceRule";
import z from "zod";
import moment from "moment";
import { CheckboxChangeEvent, FieldInputEvent, ScheduleAction } from "@lib/types";
import { ScheduleRecurrence } from "../../types";
import { showConfirmationPopup } from "../../modals/confirmationPopup/confirmationPopup";
import { provideDatepickerStyles } from "../../utils/provideDatepickerStyles";
import { providePrimaryButtonsStyles } from "../../utils/providePrimaryButtonStyles";
import { MSDataTree } from "../../../tree/tree";

// @ts-expect-error No typing for this.
import { DataTypes } from "generated";

const TIME_REGEX = /^\d\d:\d\d:\d\d$/;
const DATE_REGEX = /^\d\d\.\d\d\.\d\d\d\d$/;

const IMPULSE_DURATION_REGEX = /^\d\d:\d\d:\d\d\.\d\d\d$/;

type ParameterValueEnumOption = Readonly<{
    value: string;
    label: string;
}>;

const validationSchema = z.object({
    name: z.string().min(1, "Необходимо ввести название"),
    color: z.string().min(1),
    priority: z.number("Необходимо ввести число").min(1, "Минимальное значение 1"),
    dateTime: z.date(),
    date: z.string().regex(DATE_REGEX, "Некорректный формат"),
    time: z.string().regex(TIME_REGEX, "Некорректный формат"),
    action: z.enum(ScheduleAction),
    parameter: z.string().min(1, "Необходимо выбрать параметр"),
    parameterId: z.number().gte(0),
    impulseDuration: z.number(),
    value: z.string().min(1, "Необходимо ввести значение"),
    recurrenceRule: recurrenceRuleValidationSchema,
    isEnabled: z.boolean(),
    comment: z.string(),
    writeToArchive: z.boolean(),
    archiveMessage: z.string(),
    isSingle: z.boolean(),
})
    .superRefine((data, context) => {
        if (data.action === ScheduleAction.Impulse) {
            if (data.impulseDuration === 0) {
                context.addIssue({
                    code: "invalid_value",
                    message: "Необходимо ввести длительность импульса",
                    values: ["impulseDuration"],
                    path: ["impulseDuration"],
                });
            }
        }
    })
    .superRefine((data, context) => {
        if (!data.isSingle && data.recurrenceRule.end.type === RecurrenceEndType.Date && DATE_REGEX.test(data.date)) {
            const startDate = data.dateTime;
            const endDate = data.recurrenceRule.end.date;
            if (moment(startDate).isSameOrAfter(endDate, "day")) {
                context.addIssue({
                    code: "invalid_value",
                    message: "Дата окончания должна быть позже даты начала",
                    values: ["recurrenceRule.recurrence"],
                    path: ["recurrenceRule.recurrence"],
                });
            }
        }
    });

export type ScheduleFormValues = z.infer<typeof validationSchema>;

export type ScheduleFormSubmitEvent = CustomEvent<{
    values: ScheduleFormValues;
    submitter: HTMLElement;
}>;

const startDate = moment(new Date());

const DEFAULT_VALUES: ScheduleFormValues = {
    name: "",
    color: "2C6FD7",
    priority: 1,
    dateTime: startDate.toDate(),
    date: startDate.format("DD.MM.YYYY"),
    time: startDate.format("HH:mm:ss"),
    action: ScheduleAction.SetParameter,
    parameter: "",
    parameterId: -1,
    impulseDuration: 0,
    value: "",
    recurrenceRule: getDefaultRecurrenceRule(startDate.toDate()),
    isEnabled: true,
    comment: "",
    writeToArchive: false,
    archiveMessage: "",
    isSingle: false,
};

/** Schedule form component. */
@customElement("schedule-form")
export class ScheduleForm extends LitElement {

    private _initialValues: ScheduleFormValues;

    /** Initial values. */
    @property({ attribute: false })
    public set initialValues(value: ScheduleFormValues) {
        if (value == null) {
            return;
        }
        this._initialValues = value;
        this.form.api.reset(value);

        // Disabled state later set to correct value in "handleEditRepeatButtonClick".
        // If not setting it here to false, radio-groups in form cant see radio-buttons.
        this.editRRuleModal.disabled = false;
        this.editRRuleModal.initialValues = this.form.api.state.values.recurrenceRule;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const object = window.$pr.getObjectById(value.parameterId) as { _STType?: string };
        if (object != null) {
            const objectPath = window.$pr.getFullName(value.parameterId) as string;
            this.form.api.getFieldInfo("parameter").instance?.setValue(objectPath);

            const valueType = object._STType;
            if (valueType != null) {
                this.setParameterValueType(valueType);
            }
        } else {
            this.form.api.getFieldInfo("parameter").instance?.setErrorMap({ onSubmit: ["Параметр не найден"] });
        }
    }

    /** Initial values. */
    public get initialValues(): ScheduleFormValues {
        return this._initialValues;
    }

    /** Initial name. */
    @property({ attribute: false })
    public set initialName(value: string) {
        if (value == null) {
            return;
        }
        this.form.api.getFieldInfo("name")?.instance?.setValue(value, {
            dontUpdateMeta: true,
        });
    }

    /** Initial date. */
    @property({ attribute: false })
    public set initialDate(value: Date | null) {
        if (value == null) {
            return;
        }
        const momentDate = moment(value);
        const date = momentDate.format("DD.MM.YYYY");
        const time = momentDate.format("HH:mm:ss");
        this.form.api.getFieldInfo("dateTime")?.instance?.setValue(value, {
            dontUpdateMeta: true,
        });
        this.form.api.getFieldInfo("date")?.instance?.setValue(date, {
            dontUpdateMeta: true,
        });
        this.form.api.getFieldInfo("time")?.instance?.setValue(time, {
            dontUpdateMeta: true,
        });
    }

    @property({ type: Boolean })
    public accessor isEdit = false;

    @property({ type: Boolean })
    public accessor isSingle = false;

    @property({ type: Boolean })
    public accessor isRecurrenceEditDisabled = false;

    @query(".datepicker")
    public accessor datepicker: HTMLElement;

    @state()
    public accessor parameterValueType: "bool" | "string" | "enum" = "string";

    @state()
    public accessor parameterValueEnumOptions: readonly ParameterValueEnumOption[] = [];

    @query("#edit-parameter-button")
    private accessor editParameterButton: HTMLElement;

    @query("#edit-repeat-button")
    private accessor editRepeatButton: HTMLElement;

    @query(".delete-button")
    private accessor deleteButton: HTMLElement;

    @query(".submit-button")
    private accessor submitButton: HTMLElement;

    private readonly objectTree = new ObjectTree([], "schedule");

    private readonly editRRuleModal = new EditRepeatRuleModal(this.handleRecurrenceRuleUpdate.bind(this));

    /** Form instance. */
    protected form = new TanStackFormController(this, {
        defaultValues: DEFAULT_VALUES,
        onSubmit: ({ value }) => {
            const event: ScheduleFormSubmitEvent = new CustomEvent("scheduleFormSubmit", {
                detail: {
                    values: value,
                    submitter: this.submitButton,
                },
            });
            this.dispatchEvent(event);
        },
        validationLogic: revalidateLogic({
            mode: "submit",
            modeAfterSubmission: "change",
        }),
        validators: {
            onDynamic: validationSchema,
        },
    });

    public constructor() {
        super();

        // @ts-expect-error ObjectTree is a JS file.
        this.objectTree.onUpdate = path => {
            if (typeof path !== "string") {
                return;
            }

            // / This is how its written in VMInfo
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const object = window.$pr.getObjectByPath(path) as { _STType?: string; _ID?: number } | null;
            if (object !== null) {
                const valueType = object._STType;
                const parameterId = object._ID;
                if (valueType != null) {
                    this.setParameterValueType(valueType);
                    this.form.api.getFieldInfo("parameter")?.instance?.handleChange(path);
                    this.form.api.getFieldInfo("parameterId").instance?.handleChange(parameterId);
                    if (this.form.api.getFieldValue("action") !== ScheduleAction.Impulse) {
                        this.form.api.getFieldInfo("value")?.instance?.handleChange("");
                    }
                }
            }
        };
    }

    /** @inheritdoc */
    public static readonly styles: CSSResultGroup = scheduleFormStyles;

    /** @inheritdoc */
    public render(): TemplateResult {
        return scheduleFormTemplate.call(this);
    }

    /** @inheritdoc */
    public firstUpdated(): void {
        provideDatepickerStyles(this.datepicker);
        providePrimaryButtonsStyles([
            this.editParameterButton,
            this.editRepeatButton,
            this.submitButton,
        ]);
    }

    /** @inheritdoc */
    protected updated(): void {
        if (this.deleteButton !== null) {
            providePrimaryButtonsStyles(this.deleteButton);
        }
    }

    /** Handles edit parameters button click. */
    protected handleEditParameterButtonClick(): void {
        this.objectTree.Open(
            "Выбор параметра",
            0,
            0,
            null,
            true,
            true,
        );
        (this.objectTree.tree as MSDataTree).value = [];
    }

    /**
     * Handles date change.
     * @param value New value.
     */
    protected handleDateChange(value: string): void {
        this.form.api.getFieldInfo("date")?.instance?.handleChange(value);

        if (!DATE_REGEX.test(value)) {
            return;
        }

        const [day, month, year] = value.split(".");

        const oldValue = this.form.api.state.values.dateTime;
        const newValue = new Date(oldValue);
        newValue.setFullYear(Number(year), Number(month) - 1, Number(day));
        this.form.api.getFieldInfo("dateTime")?.instance?.handleChange(newValue);
    }

    /**
     * Handles time change.
     * @param value Value.
     */
    protected handleTimeChange(value: string): void {
        this.form.api.getFieldInfo("time")?.instance?.handleChange(value);

        if (!TIME_REGEX.test(value)) {
            return;
        }

        const [hour, minute, second] = value.split(":");

        const oldValue = this.form.api.state.values.dateTime;
        const newValue = new Date(oldValue);
        newValue.setHours(Number(hour), Number(minute), Number(second));

        this.form.api.getFieldInfo("dateTime")?.instance?.handleChange(newValue);
    }

    /**
     * Handles impulse duration change.
     * @param value Value.
     */
    protected handleImpulseDurationChange(value: string): void {
        if (!IMPULSE_DURATION_REGEX.test(value)) {
            return;
        }

        const [hour, minute, second, milliseconds] = value.split(/[.:]/);

        const totalMilliseconds =
            parseInt(hour, 10) * 60 * 60 * 1000 +
            parseInt(minute, 10) * 60 * 1000 +
            parseInt(second, 10) * 1000 +
            parseInt(milliseconds, 10);

        this.form.api.getFieldInfo("impulseDuration")?.instance?.handleChange(totalMilliseconds);
    }

    /**
     * Handles edit repeat button click.
     * @param event Mouse event.
     */
    protected handleEditRepeatButtonClick(event: MouseEvent): void {
        this.editRRuleModal.open({
            initialValues: this.form.api.state.values.recurrenceRule,
            startDate: this.form.api.state.values.dateTime,
            coords: {
                x: event.x,
                y: event.y,
            },
            isDisabled: this.isRecurrenceEditDisabled,
        });
    }

    /**
     * Handles submit.
     * @param event Submit event.
     */
    protected async handleSubmit(event: SubmitEvent): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        await this.form.api.handleSubmit();
    }

    /** Handles cancel button click. */
    protected handleCancelButtonClick(): void {
        this.dispatchEvent(new CustomEvent("scheduleFormCancel"));
    }

    /**
     * Handles delete button click.
     * @param event Mouse event.
     */
    protected handleDeleteButtonClick(event: MouseEvent): void {

        const isSingle = this.form.api.getFieldValue("recurrenceRule.recurrence") === ScheduleRecurrence.NoRepeat || this.isSingle;

        showConfirmationPopup({
            position: {
                x: event.clientX,
                y: event.clientY,
            },
            title: "Удалить",
            description: `Вы уверены, что хотите удалить ${isSingle ?
                "событие" :
                "ряд событий"}?`,
            onConfirm: () => {
                this.dispatchEvent(new CustomEvent("scheduleFormDelete"));
            },
        });
    }

    /**
     * Handles recurrence rule type change.
     * @param event Field input event with schedule recurrence type.
     */
    protected handleRecurrenceRuleChange(event: FieldInputEvent<ScheduleRecurrence>): void {
        const newValue = event.detail.value;
        this.form.api.getFieldInfo("recurrenceRule.recurrence")?.instance?.handleChange(newValue);
        this.editRRuleModal.initialValues = this.form.api.state.values.recurrenceRule;
    }

    private handleRecurrenceRuleUpdate(event: CustomEvent<RecurrenceRule>): void {
        this.form.api.setFieldValue("recurrenceRule", event.detail);
        this.editRRuleModal.close();
    }

    /**
     * Formats impulse duration.
     * @param value Duration in MS.
     */
    protected formatImpulseDuration(value: number): string {
        const date = new Date(Date.UTC(0, 0, 0, 0, 0, 0, value));

        return moment.utc(date).format("HH:mm:ss.SSS");
    }

    /**
     * Handles activation change.
     * @param event Checkbox change event.
     */
    protected handleActivationChange(event: CheckboxChangeEvent): void {
        const isActivated = event.detail.meta.checked;
        this.form.api.getFieldInfo("isEnabled")?.instance?.handleChange(isActivated);
    }

    /**
     * Sets parameter value type.
     * @param objectValueType Value type from VMInfo object.
     */
    private setParameterValueType(objectValueType: string): void {
        this.parameterValueEnumOptions = [];
        if (objectValueType === "BOOL") {
            this.parameterValueType = "bool";
        } else {
            /* eslint-disable
                @typescript-eslint/no-unsafe-assignment,
                @typescript-eslint/no-unsafe-member-access,
                @typescript-eslint/no-unsafe-call */
            const dataType = DataTypes.dataTypes[objectValueType];
            if (dataType != null && dataType.DataType !== "ElementaryType") {
                const options: ParameterValueEnumOption[] = [];
                for (const index of dataType.Indexes) {
                    const displayValue = dataType.DisplayValues[index];
                    options.push({
                        value: index.toString(),
                        label: displayValue,
                    });
                }
                this.parameterValueEnumOptions = options;
                this.parameterValueType = "enum";
            } else {
                this.parameterValueType = "string";
            }
            /* eslint-enable
                @typescript-eslint/no-unsafe-assignment,
                @typescript-eslint/no-unsafe-member-access,
                @typescript-eslint/no-unsafe-call */
        }
    }

    /** Resets form to default values. */
    public resetForm(): void {
        const date = moment(new Date());
        this.form.api.reset({
            ...DEFAULT_VALUES,
            recurrenceRule: getDefaultRecurrenceRule(date.toDate()),
            dateTime: date.toDate(),
            date: date.format("DD.MM.YYYY"),
            time: date.format("HH:mm:ss"),
        });
        this.parameterValueType = "string";
    }

    /** Closes child modals. */
    public closeChildModals(): void {
        this.objectTree.dispose();
        this.editRRuleModal.close();
    }
}
